import React, {PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Text} from '../../atoms/Text/Text';
import {COLORS} from '../../../constants/colors';
import {IconName} from '../../atoms/Icon/IconName';
import {WithBadge} from '../../atoms/Badge/WithBadge';
import {tabletAndUp} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';

export type HeaderButtonProps = PropsWithChildren<{
  asset: IconName;
  onClick?: React.MouseEventHandler;
  count?: number;
  showBadge?: boolean;
  badgeColor?: string;
  'aria-label'?: string;
  badgeOffset?: {right?: number; top?: number};
  to?: string;
  ['data-testid']?: string;
  isCheckout?: boolean;
  iconSize?: keyof typeof IconSize;
  trackClick?: VoidFunction;
  className?: string;
  iconRef?: React.Ref<HTMLElement>;
}>;

export const HeaderButton = React.forwardRef<
  HTMLButtonElement,
  HeaderButtonProps
>(function HeaderButton(
  {
    asset,
    count,
    badgeOffset,
    badgeColor,
    showBadge,
    onClick,
    to,
    isCheckout,
    iconSize,
    trackClick,
    iconRef,
    ...rest
  },
  ref,
) {
  const props = to
    ? {
        as: UnstyledLink,
        to,
        onClick: trackClick,
      }
    : {
        onClick: (e: React.MouseEvent) => {
          trackClick?.();
          onClick?.(e);
        },
      };

  if (isCheckout) {
    return null;
  }

  return (
    <ButtonBase
      {...props}
      {...rest}
      $iconWidth={iconSize ? IconSize[iconSize] : IconSize.S}
      ref={ref}
    >
      <WithBadge
        variant={'rounded'}
        offset={{
          top: badgeOffset?.top ?? 0,
          right: badgeOffset?.right ?? 0,
        }}
        badgeSize={7}
        badgeColor={badgeColor}
        displayBadgeWithoutCount={showBadge}
        ref={iconRef}
      >
        <HeaderButtonIcon
          data-testid="filterFlyoutSectionNextIcon"
          asset={asset}
          fill={COLORS.Black}
          size={iconSize}
        />
      </WithBadge>
      {Boolean(count) && (
        <CountText data-testid="numberBadge">{count}</CountText>
      )}
    </ButtonBase>
  );
});

const ButtonBase = styled(UnstyledButton)<{
  $iconWidth: number;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 8px;
  min-width: ${props => props.$iconWidth}px;
  text-align: center;

  ${tabletAndUp(css`
    margin: 0;
    gap: 1px;
  `)}
`;

const HeaderButtonIcon = styled(Icon)<{size?: keyof typeof IconSize}>`
  ${({size}) =>
    !size &&
    css`
      width: 20px;
      height: 16px;
    `}
`;

const CountText = styled(Text)`
  font-size: ${FONT_SIZES.px9};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};

  ${tabletAndUp(css`
    font-size: ${FONT_SIZES.px10};
    line-height: 10px;
  `)}
`;
